
import apiClient from "../../helpers/axios";

/*
 * Генератор вывесок (позже — визиток).
 * BACKEND: controller/printSign.controller.js, маршруты в routes/printSign.js.
 *
 *   Профиль бренда — данные фирмы: название, логотип, контакты, режим работы, услуги, преимущества.
 *   Вывеска — как выглядит: размер, дизайн, блоки, фоны. Из профиля + вывески сервер рисует PNG и PDF.
 *   Библиотека — фоны, фото для блока преимуществ (рендер памятников), свои иконки.
 */
const base = "/admin/print-signs";

export const BLOCK_TYPES = ["header", "features", "services", "contacts"];
export const SHOW_KEYS = ["logo", "name", "tagline", "phones", "emails", "addresses", "hours", "qr", "button"];
export const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
export const DAY_LABELS = { mon: "Пн", tue: "Вт", wed: "Ср", thu: "Чт", fri: "Пт", sat: "Сб", sun: "Вс" };

const DEFAULT_DAY = { mode: "open", from: "10:00", to: "18:00", breakFrom: "", breakTo: "" };

export const DEFAULT_SCHEDULE = {
    mon: { ...DEFAULT_DAY },
    tue: { ...DEFAULT_DAY },
    wed: { ...DEFAULT_DAY },
    thu: { ...DEFAULT_DAY },
    fri: { ...DEFAULT_DAY },
    sat: { ...DEFAULT_DAY, to: "15:00" },
    sun: { ...DEFAULT_DAY, mode: "closed" },
};

const IMAGE_DEFAULTS = { assetId: null, scale: 1, offsetX: 0, offsetY: 0, darken: 0 };

export const EMPTY_SIGN_DRAFT = {
    name: "",
    profileId: null,
    size: { preset: "door-sign", widthMm: 600, heightMm: 1400, bleedMm: 3, cropMarks: false },
    design: "granite-gold",
    blocks: BLOCK_TYPES.map((type) => ({ type, visible: true })),
    show: SHOW_KEYS.reduce((result, key) => ({ ...result, [key]: true }), {}),
    servicesColumns: 0,
    background: { ...IMAGE_DEFAULTS },
    featuresImage: { ...IMAGE_DEFAULTS, fade: true },
    // Шапка: размеры логотипа, названия и подписи (1 — как в дизайне) и их сдвиг (доли шапки).
    header: { logoScale: 1, nameScale: 1, taglineScale: 1, logoX: 0, logoY: 0, textX: 0, textY: 0, taglineX: 0, taglineY: 0 },
    // Дизайн «Свои цвета»: фон, металл, текст (пусто — сам), фактура.
    colors: { background: "#101214", accent: "#c9a35a", text: "", texture: "stone" },
    // Высоты блоков из редактора (null — как задумано дизайном) и размер текста.
    layout: { weights: null, textScale: 1 },
};

export const EMPTY_PROFILE_DRAFT = {
    name: "",
    brandName: "",
    tagline: "",
    phones: [],
    emails: [],
    addresses: [],
    site: "",
    qrUrl: "",
    qrCaption: "Сканируйте для перехода в онлайн-офис",
    schedule: DEFAULT_SCHEDULE,
    hoursTitle: "Режим работы:",
    servicesTitle: "Наши услуги",
    services: [],
    featuresTitle: "",
    features: [],
    buttonText: "Оформить заказ",
    buttonIcon: "arrow",
};

/** Только то, что можно менять в вывеске, — это и уходит на сервер. */
export const toSignDraft = (sign) => ({
    name: sign.name || "",
    profileId: sign.profileId || null,
    size: { ...EMPTY_SIGN_DRAFT.size, ...(sign.size || {}) },
    design: sign.design || "granite-gold",
    blocks: Array.isArray(sign.blocks) && sign.blocks.length
        ? sign.blocks.map((block) => ({ type: block.type, visible: block.visible !== false }))
        : EMPTY_SIGN_DRAFT.blocks,
    show: { ...EMPTY_SIGN_DRAFT.show, ...(sign.show || {}) },
    servicesColumns: Number(sign.servicesColumns || 0),
    background: { ...IMAGE_DEFAULTS, ...(sign.background || {}) },
    featuresImage: { ...EMPTY_SIGN_DRAFT.featuresImage, ...(sign.featuresImage || {}) },
    header: {
        logoScale: Number(sign.header?.logoScale || 1),
        nameScale: Number(sign.header?.nameScale || 1),
        taglineScale: Number(sign.header?.taglineScale || 1),
        logoX: Number(sign.header?.logoX || 0),
        logoY: Number(sign.header?.logoY || 0),
        textX: Number(sign.header?.textX || 0),
        textY: Number(sign.header?.textY || 0),
        taglineX: Number(sign.header?.taglineX || 0),
        taglineY: Number(sign.header?.taglineY || 0),
    },
    colors: { ...EMPTY_SIGN_DRAFT.colors, ...(sign.colors || {}) },
    layout: {
        weights: sign.layout?.weights || null,
        textScale: Number(sign.layout?.textScale || 1),
    },
});

export const toProfileDraft = (profile) => ({
    name: profile.name || "",
    brandName: profile.brandName || "",
    tagline: profile.tagline || "",
    phones: [...(profile.phones || [])],
    emails: [...(profile.emails || [])],
    addresses: [...(profile.addresses || [])],
    site: profile.site || "",
    qrUrl: profile.qrUrl || "",
    qrCaption: profile.qrCaption || "",
    schedule: DAYS.reduce((result, day) => ({
        ...result,
        [day]: { ...DEFAULT_SCHEDULE[day], ...((profile.schedule || {})[day] || {}) },
    }), {}),
    hoursTitle: profile.hoursTitle || "",
    servicesTitle: profile.servicesTitle || "",
    services: (profile.services || []).map((item) => ({ icon: item.icon || "star", assetId: item.assetId || null, text: item.text || "" })),
    featuresTitle: profile.featuresTitle || "",
    features: (profile.features || []).map((item) => ({
        icon: item.icon || "star", assetId: item.assetId || null, value: item.value || "", text: item.text || "",
    })),
    buttonText: profile.buttonText || "",
    buttonIcon: profile.buttonIcon || "arrow",
});

/* Режим работы — так же, как его пишет сервер (utils/signSchedule.js), для подсказки в редакторе. */
const describeDay = (day) => {
    if (day.mode === "closed") return "выходной";
    if (day.mode === "allday") return "круглосуточно";
    const baseText = `${day.from || "—"} – ${day.to || "—"}`;
    return day.breakFrom && day.breakTo ? `${baseText}, перерыв ${day.breakFrom} – ${day.breakTo}` : baseText;
};

export const formatSchedule = (schedule = {}) => {
    const texts = DAYS.map((day) => describeDay({ ...DEFAULT_SCHEDULE[day], ...(schedule[day] || {}) }));
    if (texts.every((text) => text === texts[0])) {
        return texts[0] === "круглосуточно" ? ["Ежедневно, круглосуточно"] : [`Ежедневно: ${texts[0]}`];
    }
    const groups = [];
    DAYS.forEach((day, index) => {
        const last = groups[groups.length - 1];
        if (last && last.text === texts[index]) last.to = day;
        else groups.push({ from: day, to: day, text: texts[index] });
    });
    return groups.map((group) => {
        const label = group.from === group.to ? DAY_LABELS[group.from] : `${DAY_LABELS[group.from]} – ${DAY_LABELS[group.to]}`;
        return `${label}: ${group.text}`;
    });
};

/**
 * Текст ошибки сервера. Когда ждали файл (PNG, PDF), ответ с ошибкой
 * тоже приходит «файлом» — его нужно сначала прочитать.
 */
export const readApiError = async (error, fallback) => {
    const data = error?.response?.data;
    if (typeof Blob !== "undefined" && data instanceof Blob) {
        try {
            const parsed = JSON.parse(await data.text());
            if (parsed?.message) return String(parsed.message);
        } catch (_error) {
            return fallback;
        }
    }
    return String(data?.message || fallback || error?.message || "Ошибка");
};

const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Не удалось прочитать файл"));
    reader.readAsDataURL(file);
});

/* Справочник */

export const getSignMeta = async () => (await apiClient.get(`${base}/meta`)).data;

/* Профили бренда */

export const listProfiles = async () => {
    const response = await apiClient.get(`${base}/profiles`);
    return Array.isArray(response.data?.profiles) ? response.data.profiles : [];
};
export const getProfile = async (profileId) => (await apiClient.get(`${base}/profiles/${profileId}`)).data.profile;
export const createProfile = async (draft) => (await apiClient.post(`${base}/profiles`, draft)).data.profile;
export const updateProfile = async (profileId, draft) => (await apiClient.patch(`${base}/profiles/${profileId}`, draft)).data.profile;
export const deleteProfile = async (profileId) => (await apiClient.delete(`${base}/profiles/${profileId}`)).data;
export const duplicateProfile = async (profileId) => (await apiClient.post(`${base}/profiles/${profileId}/duplicate`)).data.profile;
export const uploadProfileLogo = async (profileId, file) => (
    await apiClient.post(`${base}/profiles/${profileId}/logo`, { image: await readFileAsBase64(file) })
).data.profile;
export const deleteProfileLogo = async (profileId) => (await apiClient.delete(`${base}/profiles/${profileId}/logo`)).data.profile;
export const fetchProfileLogo = async (profileId) => (
    await apiClient.get(`${base}/profiles/${profileId}/logo`, { responseType: "blob", params: { t: Date.now() } })
).data;

/* Библиотека картинок */

export const listAssets = async (kind = "") => {
    const response = await apiClient.get(`${base}/assets`, { params: kind ? { kind } : {} });
    return Array.isArray(response.data?.assets) ? response.data.assets : [];
};
export const createAsset = async ({ kind, name, file }) => (
    await apiClient.post(`${base}/assets`, { kind, name, image: await readFileAsBase64(file) })
).data.asset;
export const renameAsset = async (assetId, name) => (await apiClient.patch(`${base}/assets/${assetId}`, { name })).data.asset;
export const deleteAsset = async (assetId) => (await apiClient.delete(`${base}/assets/${assetId}`)).data;
export const fetchAssetImage = async (assetId, width = 360) => (
    await apiClient.get(`${base}/assets/${assetId}/file`, { responseType: "blob", params: { w: width } })
).data;

/* Вывески */

export const listSigns = async () => {
    const response = await apiClient.get(base);
    return Array.isArray(response.data?.signs) ? response.data.signs : [];
};
export const getSign = async (signId) => (await apiClient.get(`${base}/${signId}`)).data.sign;
export const createSign = async (draft) => (await apiClient.post(base, draft)).data.sign;
export const updateSign = async (signId, draft) => (await apiClient.patch(`${base}/${signId}`, draft)).data.sign;
export const deleteSign = async (signId) => (await apiClient.delete(`${base}/${signId}`)).data;
export const duplicateSign = async (signId) => (await apiClient.post(`${base}/${signId}/duplicate`)).data.sign;
/** Собрать PDF и PNG. Сервер отвечает, когда файлы готовы (обычно несколько секунд). */
export const buildSign = async (signId) => (await apiClient.post(`${base}/${signId}/build`, {}, { timeout: 180000 })).data.sign;
export const fetchSignFile = async (signId, fileId, format) => (
    await apiClient.get(`${base}/${signId}/files/${fileId}/${format}`, { responseType: "blob" })
).data;

/**
 * PNG-предпросмотр по тому, что сейчас на экране (сохранять не нужно).
 * Можно передать черновик вывески, черновик профиля или оба сразу.
 */
export const previewSign = async ({ signId = "", sign = null, profileId = "", profile = null, width = 900 }) => {
    const response = await apiClient.post(
        `${base}/preview`,
        { signId, sign, profileId, profile, width },
        { responseType: "blob", timeout: 120000 }
    );
    let warnings = [];
    let layout = null;
    try {
        warnings = JSON.parse(decodeURIComponent(response.headers?.["x-sign-warnings"] || "%5B%5D"));
    } catch (_error) {
        warnings = [];
    }
    try {
        // Где стоят блоки (мм) — по этому рисуются рамки поверх картинки.
        layout = JSON.parse(decodeURIComponent(response.headers?.["x-sign-layout"] || "null"));
    } catch (_error) {
        layout = null;
    }
    return { blob: response.data, warnings, layout };
};

/* JSON: выгрузка и загрузка (как у КП и смет) */

const readJsonFilename = (headers, fallback) => {
    const match = String(headers?.["content-disposition"] || "").match(/filename\*=UTF-8''([^;]+)/i);
    try {
        return match ? decodeURIComponent(match[1]) : fallback;
    } catch (_error) {
        return fallback;
    }
};

/** Вывеска и её профиль в JSON-файл. */
export const exportSignJson = async (signId) => {
    const response = await apiClient.get(`${base}/${signId}/export`, { responseType: "blob" });
    return { blob: response.data, filename: readJsonFilename(response.headers, "vyveska.json") };
};

/** Только профиль в JSON-файл. */
export const exportProfileJson = async (profileId) => {
    const response = await apiClient.get(`${base}/profiles/${profileId}/export`, { responseType: "blob" });
    return { blob: response.data, filename: readJsonFilename(response.headers, "profil.json") };
};

/**
 * Загрузить JSON. Без signId/profileId — новый профиль и новая вывеска;
 * с signId — в эту вывеску и её профиль; с profileId — в профиль.
 * Возвращает { profile, sign, notes }.
 */
export const importSignJson = async ({ data, signId = "", profileId = "" }) => (
    await apiClient.post(`${base}/import`, { data, signId, profileId }, { timeout: 120000 })
).data;

/** Прочитать JSON-файл с диска (с проверкой размера). */
export const readJsonFile = async (file) => {
    if (file.size > 25 * 1024 * 1024) throw new Error("Файл больше 25 МБ.");
    return file.text();
};
